import type { AgentAnalysisResult } from "@/types/communications";
import type { ResolvedClickUpMeeting } from "@/lib/clickup/resolveClickUpProject";

export function buildChatRefinementsSummary(
  messages: Array<{ role: string; content: string }>,
  maxMessages = 12
): string {
  const recent = messages
    .filter((m) => m.content?.trim() && (m.role === "user" || m.role === "assistant"))
    .slice(-maxMessages);

  if (recent.length === 0) return "";

  return recent
    .map((m) => `**${m.role === "user" ? "You" : "Assistant"}:** ${m.content.trim()}`)
    .join("\n\n");
}

export function mergeChatIntoResults(
  results: AgentAnalysisResult[],
  chatRefinements: string
): AgentAnalysisResult[] {
  if (!chatRefinements.trim()) return results;

  const block = `\n\n---\n## Chat discussion refinements\n${chatRefinements.trim()}`;

  return results.map((r) => ({
    ...r,
    content: r.content ? `${r.content}${block}` : chatRefinements.trim(),
  }));
}

export function mergeChatIntoClickUpMeetings(
  meetings: ResolvedClickUpMeeting[],
  chatRefinements: string
): ResolvedClickUpMeeting[] {
  if (!chatRefinements.trim()) return meetings;

  return meetings.map((m) => ({
    ...m,
    proposedDeliverables: m.proposedDeliverables.map((d, i) =>
      i === 0
        ? {
            ...d,
            description: [d.description, `\n\nChat refinements:\n${chatRefinements.trim()}`]
              .filter(Boolean)
              .join(""),
          }
        : d
    ),
  }));
}

export function appendChatToItemBodies<T extends { body: string }>(
  items: T[],
  chatRefinements: string
): T[] {
  if (!chatRefinements.trim()) return items;

  const block = `\n\n---\nChat discussion refinements:\n${chatRefinements.trim()}`;

  return items.map((item) => ({
    ...item,
    body: `${item.body}${block}`,
  }));
}

export function buildHandoffChatSeed(session: {
  agentName: string;
  source: string;
  items: Array<{ label: string; subject?: string }>;
  results: AgentAnalysisResult[];
  clickUpContext?: { meetings: ResolvedClickUpMeeting[] };
}): string {
  const lines: string[] = [
    `I'd like to discuss the results from **${session.agentName}** on ${session.source}.`,
    "",
    "### Source items",
    ...session.items.map((i) => `- ${i.label || i.subject || "Item"}`),
    "",
    "### Agent output",
  ];

  for (const r of session.results) {
    if (r.error) continue;
    lines.push(`**${r.itemLabel}**`);
    lines.push(r.content.trim() || "(no content)");
    lines.push("");
  }

  if (session.clickUpContext?.meetings.length) {
    lines.push("### Proposed ClickUp tasks");
    for (const m of session.clickUpContext.meetings) {
      lines.push(`**${m.itemLabel}** → ${m.matchedListName}`);
      for (const d of m.proposedDeliverables.filter((x) => x.selected)) {
        lines.push(`- ${d.title}${d.owner ? ` (${d.owner})` : ""}`);
      }
    }
    lines.push("");
  }

  lines.push("Please help me refine these before I post or take action.");

  return lines.join("\n");
}
